import { useApiMutation, useApiQuery } from './useApi';
import { useQueryClient } from 'react-query';
import { invalidateCrmQueries } from './useCrm';

// #################################################
//                      TYPES
// #################################################

export type NootaMeetingStatus = 'pending' | 'attached' | 'auto_ignored' | 'dismissed' | 'failed';

/**
 * Une réunion Noota telle que la file la présente.
 *
 * Ni le compte-rendu ni le verbatim n'y figurent : le titre et les participants
 * suffisent à décider à qui rattacher, et une liste de 110 lignes portant chacune
 * plusieurs milliers de caractères serait illisible.
 */
export type NootaMeeting = {
    id: number;
    noota_record_id: string;
    title: string | null;
    meeting_url: string | null;
    owner_email: string | null;
    started_at: string | null;
    duration_minutes: number | null;
    status: NootaMeetingStatus;
    error_message: string | null;
    external_participants: string[];
    domains: string[];
    attached_to?: { type: string; id: number; name: string | null };
    facility_note_id: number | null;
    resolved_at: string | null;
    suggestions: NootaMeetingSuggestion[];
};

export type NootaMeetingSuggestion = {
    type: string;
    id: number;
    name: string | null;
    reason: string;
};

export type NootaMeetingFilters = {
    status?: NootaMeetingStatus | 'all';
    owner_email?: string;
    search?: string;
    per_page?: number;
};

export type NootaTarget = {
    type: 'facility' | 'prospect';
    id: number;
};

// #################################################
//                      QUERIES
// #################################################

export function useNootaMeetings(filters: NootaMeetingFilters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.append(key, String(value));
    });
    const query = params.toString();

    return useApiQuery<{ data: NootaMeeting[]; meta?: { total: number } }>(
        `/crm/noota-meetings${query ? `?${query}` : ''}`
    );
}

/**
 * Le compte rendu, chargé à la demande au dépliage d'une ligne de la file.
 *
 * Hors de `NootaMeeting` à dessein : 110 lignes portant chacune 5 à 12 Ko
 * seraient plusieurs mégaoctets par page, pour un contenu qu'on ne lit que sur
 * une poignée de lignes. Le verbatim, lui, n'est jamais exposé.
 */
export type NootaMeetingReport = {
    id: number;
    report_html: string | null;
    report_plaintext: string | null;
};

export function useNootaMeetingReport(meetingId: number | null) {
    return useApiQuery<NootaMeetingReport>(
        meetingId ? `/crm/noota-meetings/${meetingId}/report` : '',
        {},
        { enabled: !!meetingId, staleTime: 5 * 60 * 1000 }
    );
}

/**
 * Compteurs par statut — alimentent la pastille du menu et les onglets.
 *
 * Le `enabled` évite d'appeler une route CRM pour un utilisateur qui n'y a pas accès :
 * la réponse serait un 403 à chaque chargement de page.
 */
export function useNootaMeetingCounts(enabled = true, refetchInterval: number | false = 60000) {
    return useApiQuery<Record<string, number>>('/crm/noota-meetings/counts', {}, {
        enabled,
        refetchInterval,
    });
}

// #################################################
//                     MUTATIONS
// #################################################

/**
 * Associe une réunion à une fiche.
 *
 * `learn_domain` (activé par défaut côté API) enregistre la correspondance
 * domaine → fiche : les réunions suivantes du même domaine se rattacheront seules.
 * C'est ce qui fait descendre la file au lieu de la laisser stagner.
 */
export function useAssociateNootaMeeting() {
    const queryClient = useQueryClient();

    return useApiMutation<NootaMeeting, Error, NootaTarget & { meetingId: number; learn_domain?: boolean }>(
        'post',
        (vars) => `/crm/noota-meetings/${vars.meetingId}/associate`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Rattache d'un geste toutes les réunions en attente d'un même domaine. */
export function useBulkAssociateNootaDomain() {
    const queryClient = useQueryClient();

    return useApiMutation<{ domain: string; attached: number }, Error, NootaTarget & { domain: string }>(
        'post',
        () => '/crm/noota-meetings/bulk-associate-domain',
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Écarte une réunion — elle reste consultable, rien n'est supprimé. */
export function useIgnoreNootaMeeting() {
    const queryClient = useQueryClient();

    return useApiMutation<NootaMeeting, Error, { meetingId: number }>(
        'post',
        (vars) => `/crm/noota-meetings/${vars.meetingId}/ignore`,
        { onSuccess: () => invalidateCrmQueries(queryClient) }
    );
}

/** Crée un prospect pré-rempli depuis la réunion, puis y pose le compte-rendu. */
export function useCreateProspectFromNootaMeeting() {
    const queryClient = useQueryClient();

    return useApiMutation<
        { prospect_id: number; meeting: NootaMeeting },
        Error,
        {
            meetingId: number;
            name: string;
            contact_email?: string;
            contact_name?: string;
            pipeline_stage_id?: number;
        }
    >('post', (vars) => `/crm/noota-meetings/${vars.meetingId}/create-prospect`, {
        onSuccess: () => invalidateCrmQueries(queryClient),
    });
}
